'use client';

import React from 'react';
import { Box, FormLabel, Stack, TextField, Typography } from '@mui/material';
import type { IntakeFieldErrors, IntakeFieldName } from '../intake-validation';
import type { IntakeFormValues } from './IntakeFields';
import { INTAKE_PLACEHOLDERS, intakeFieldLabelSx } from '../intake-copy';
import { getActiveElementIn } from '../../dialogFocus';
import { IntakeDialCodeField } from './IntakeDialCodeField';

type Props = {
  values: IntakeFormValues;
  detectedIso: string | null;
  errors: IntakeFieldErrors;
  onChange: (field: keyof IntakeFormValues, value: string | boolean) => void;
  onBlurField: (field: IntakeFieldName) => void;
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'background.paper',
    borderRadius: 1,
  },
};

const PHONE_LABEL_ID = 'intake-phone-label';

function countDigits(text: string): number {
  return text.replace(/\D/g, '').length;
}

/** Caret index right after the n-th digit (0 when n is 0, end when n covers all). */
function caretAfterDigits(text: string, n: number): number {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i++) {
    if (/\d/.test(text[i])) seen++;
    if (seen === n) return i + 1;
  }
  return text.length;
}

export function IntakePhoneFields({
  values,
  detectedIso,
  errors,
  onChange,
  onBlurField,
}: Props) {
  const phoneError = errors.phoneCountryIso || errors.phone;
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  // Digits that must stay left of the caret once the value has been reformatted.
  const caretDigitsRef = React.useRef<number | null>(null);
  const [, setReformat] = React.useState(0);

  function change(raw: string, digitsBeforeCaret: number) {
    caretDigitsRef.current = digitsBeforeCaret;
    onChange('phone', raw);
    // Re-render even when the formatted value is unchanged so the caret is restored.
    setReformat(n => n + 1);
  }

  React.useLayoutEffect(() => {
    const digits = caretDigitsRef.current;
    const input = inputRef.current;
    caretDigitsRef.current = null;
    if (digits === null || !input) return;
    // Autofill can change the field while focus is elsewhere; do not move focus or scroll.
    if (getActiveElementIn(input) !== input) return;
    const pos = caretAfterDigits(input.value, digits);
    input.setSelectionRange(pos, pos);
  });

  // Backspace/Delete on a formatting character would be re-added by the mask:
  // remove the neighbouring digit instead.
  function onPhoneKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;
    if (event.altKey || event.metaKey || event.ctrlKey) return;
    const input = event.currentTarget;
    const { value, selectionStart, selectionEnd } = input;
    if (selectionStart === null || selectionStart !== selectionEnd) return;
    const backward = event.key === 'Backspace';
    const removed = backward
      ? value[selectionStart - 1]
      : value[selectionStart];
    if (removed === undefined || /\d/.test(removed)) return;
    let digitIndex = -1;
    if (backward) {
      for (let i = selectionStart - 2; i >= 0; i--) {
        if (/\d/.test(value[i])) {
          digitIndex = i;
          break;
        }
      }
    } else {
      for (let i = selectionStart + 1; i < value.length; i++) {
        if (/\d/.test(value[i])) {
          digitIndex = i;
          break;
        }
      }
    }
    event.preventDefault();
    if (digitIndex < 0) return;
    change(
      value.slice(0, digitIndex) + value.slice(digitIndex + 1),
      countDigits(value.slice(0, digitIndex))
    );
  }

  return (
    <Box role="group" aria-labelledby={PHONE_LABEL_ID}>
      <FormLabel
        sx={intakeFieldLabelSx}
        id={PHONE_LABEL_ID}
        htmlFor="intake-phone"
      >
        Phone number
      </FormLabel>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <IntakeDialCodeField
          value={values.phoneCountryIso}
          detectedIso={detectedIso}
          error={errors.phoneCountryIso}
          errorId={phoneError ? 'intake-phone-help' : undefined}
          onChange={iso2 => onChange('phoneCountryIso', iso2)}
          onBlur={() => onBlurField('phoneCountryIso')}
        />
        <TextField
          id="intake-phone"
          name="phone"
          type="tel"
          autoComplete="tel-national"
          required
          size="small"
          placeholder={INTAKE_PLACEHOLDERS.phone}
          value={values.phone}
          onChange={e =>
            change(
              e.target.value,
              countDigits(
                e.target.value.slice(0, e.target.selectionStart ?? undefined)
              )
            )
          }
          onBlur={() => onBlurField('phone')}
          error={Boolean(errors.phone)}
          aria-invalid={Boolean(errors.phone)}
          sx={{ ...fieldSx, flex: '1 1 58%' }}
          inputRef={inputRef}
          inputProps={{
            onKeyDown: onPhoneKeyDown,
            inputMode: 'tel',
            'aria-describedby': phoneError ? 'intake-phone-help' : undefined,
          }}
        />
      </Stack>
      {phoneError && (
        <Typography
          id="intake-phone-help"
          variant="caption"
          color="error"
          sx={{ display: 'block', mt: 0.75, fontSize: 12 }}
        >
          {phoneError}
        </Typography>
      )}
    </Box>
  );
}
